/**
 * [Weight Rebalance + Edge Surfaces §2b + §2c] EdgeListsSection
 *
 * Two side-by-side lists on the Dashboard:
 *
 *   • PGF Finds   - Top-N UNDERVALUED (green badge)
 *   • PGF Flags   - Top-N OVERVALUED  (orange badge)
 *
 * Renamed 2026-07-30 per founder Addendum 2: "Busts and Sleepers"
 * terminology is done in the market; PGF uses Fades and Finds.
 * "Sleepers" -> "Finds" here. "Flags" title unchanged (kept the
 * neutral label; overvalued backend endpoint unchanged).
 *
 * Product gate: currently admin-only (see canSeeLists below). Founder
 * pauses Finds/Fades render for INTERNAL review while the composite
 * trust check is pending. Copy is template-driven from the backend
 * `pgf_edge` object.
 *
 * Draft-context aware: if a `leagueId` for an active draft is provided,
 * the backend excludes already-drafted players.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown, ChevronRight, User } from "lucide-react";import { authHeaders, useAuth } from "../../contexts/AuthContext";
import { useEntitlements } from "../../hooks/useEntitlements";
import { PGFEdgeBadge } from "../analytics/PGFEdgeBadge";
import { UnlistedPill } from "../analytics/AdpDisplay";
import EdgePlayerModal from "./EdgePlayerModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SEASON = 2026;
const DEFAULT_FORMAT = "half_ppr";

async function fetchEdgeList(kind, { seasonYear, scoringFormat, leagueId, limit }) {
  const params = new URLSearchParams({
    season_year: String(seasonYear),
    scoring_format: scoringFormat,
    limit: String(limit),
  });
  if (leagueId) params.set("league_id", leagueId);
  const { data } = await axios.get(`${API}/analytics/edge/${kind}?${params.toString()}`, {
    headers: authHeaders(),
  });
  return data[kind] || [];
}

function EdgeRow({ item, isDark, onOpen }) {
  const bg = isDark ? "bg-[#161616] hover:bg-[#1c1c1c]" : "bg-white hover:bg-gray-50";
  const border = isDark ? "border-white/[0.06]" : "border-black/[0.06]";
  const text = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const sub = isDark ? "text-white/60" : "text-black/60";
  const [imgErr, setImgErr] = useState(false);
  const photoUrl = item.photo_url;
  return (
    <button
      type="button"
      onClick={() => onOpen?.(item)}
      className={`w-full text-left flex items-start gap-3 rounded-lg border ${border} ${bg} px-3 py-2 transition-colors cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#FF6B00]/50`}
      data-testid={`edge-row-${item.player_id}`}
    >
      {imgErr || !photoUrl ? (
        <div
          className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0"
          data-testid={`edge-row-photo-${item.player_id}`}
        >
          <User className="w-4 h-4 text-white/40" />
        </div>
      ) : (
        <img
          src={photoUrl}
          alt={item.name}
          onError={() => setImgErr(true)}
          className="w-8 h-8 rounded-full object-cover bg-white/5 shrink-0"
          data-testid={`edge-row-photo-${item.player_id}`}
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <div className={`font-display font-bold text-sm ${text} truncate`}>
            {item.name}
          </div>
          <div className={`font-mono text-[10px] ${sub}`}>
            {item.position} · {item.nfl_team || "FA"}
          </div>
        </div>
        <div className={`mt-0.5 font-mono text-[11px] ${sub}`}>
          ADP:{" "}
          {item.adp_position_label ? (
            <span className={text}>{item.adp_position_label}</span>
          ) : (
            <UnlistedPill />
          )}{" "}
          <span className="opacity-60">→</span>{" "}
          PGF:{" "}
          <span className={text}>{item.pgf_position_label}</span>
        </div>
        <div className={`mt-1 text-[10px] ${sub}`} data-testid={`edge-row-driver-${item.player_id}`}>
          {item.driving_axis ? (
            <>
              <span className="uppercase tracking-widest">Edge driver:</span>{" "}
              <span className={`${text} lowercase`}>{item.driving_axis}</span>
            </>
          ) : (
            <span className="uppercase tracking-widest opacity-60">Edge driver: n/a</span>
          )}
        </div>
      </div>
      <PGFEdgeBadge
        edge={{
          badge: item.badge,
          pgf_value: item.pgf_value,
          market_value: item.market_value,
          edge_dollars: item.edge_dollars,
          edge_pct: item.edge_pct,
          driving_axis: item.driving_axis,
        }}
        size="sm"
        compact
        asChild
      />
    </button>
  );
}

function EdgeList({ title, subtitle, icon, items, loading, isDark, testid, onOpen }) {
  const text = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const sub = isDark ? "text-white/50" : "text-black/50";
  return (
    <div className="flex-1 min-w-0" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <div>
          <h3 className={`font-display font-bold text-sm uppercase tracking-wide ${text}`}>
            {title}
          </h3>
          <p className={`font-mono text-[10px] ${sub}`}>{subtitle}</p>
        </div>
      </div>
      <div className="space-y-1.5">
        {loading && (
          <div className={`text-xs ${sub} font-mono py-4 text-center`}>
            Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className={`text-xs ${sub} font-mono py-4 text-center`}>
            No divergence today. PGF and the market agree on the top of the board.
          </div>
        )}
        {!loading && items.map((it) => (
          <EdgeRow key={it.player_id} item={it} isDark={isDark} onOpen={onOpen} />
        ))}
      </div>
    </div>
  );
}

export default function EdgeListsSection({
  leagueId = null,
  seasonYear = DEFAULT_SEASON,
  scoringFormat = DEFAULT_FORMAT,
  limit = 8,
  isDark = true,
}) {
  const { loading: entLoading } = useEntitlements();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Product gate 2026-07-31: Finds/Fades render for INTERNAL / admin
  // review only, pending the composite trust check. Non-admin users
  // see the section container so the Dashboard layout does not shift,
  // but the lists themselves render an admin-only placeholder. Flip
  // this off (or gate on entitlements alone) once the composite trust
  // check ships.
  const canSeeLists = isAdmin;
  const [finds, setFinds] = useState([]);
  const [overvalued, setOvervalued] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (entLoading) return;
    if (!canSeeLists) {
      setLoading(false);
      setFinds([]);
      setOvervalued([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchEdgeList("finds", { seasonYear, scoringFormat, leagueId, limit }),
      fetchEdgeList("overvalued", { seasonYear, scoringFormat, leagueId, limit }),
    ])
      .then(([fd, ov]) => {
        if (cancelled) return;
        setFinds(fd);
        setOvervalued(ov);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Edge feed unavailable");
        setFinds([]);
        setOvervalued([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [entLoading, canSeeLists, seasonYear, scoringFormat, leagueId, limit]);

  const bg = isDark ? "bg-[#0F0F0F]" : "bg-white/50";
  const border = isDark ? "border-white/[0.08]" : "border-black/[0.08]";

  const [selected, setSelected] = useState(null);

  return (
    <section
      className={`relative rounded-2xl border ${border} ${bg} p-4 sm:p-5 mb-6`}
      data-testid="edge-lists-section"
    >
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${canSeeLists ? "" : "pointer-events-none blur-sm select-none opacity-70"}`}>
        <EdgeList
          title="PGF Finds"
          subtitle="Undervalued by the market"
          icon={<TrendingUp className="w-4 h-4 text-[#00C853]" data-testid="edge-icon-finds" />}
          items={finds}
          loading={loading || entLoading}
          isDark={isDark}
          testid="edge-list-finds"
          onOpen={setSelected}
        />
        <EdgeList
          title="PGF Flags"
          subtitle="Overvalued by the market"
          icon={<TrendingDown className="w-4 h-4 text-[#FF6B00]" data-testid="edge-icon-overvalued" />}
          items={overvalued}
          loading={loading || entLoading}
          isDark={isDark}
          testid="edge-list-overvalued"
          onOpen={setSelected}
        />
      </div>
      {!canSeeLists && !entLoading && (
        <div
          className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/40 backdrop-blur-sm"
          data-testid="edge-lists-admin-gate"
        >
          <div className="text-center px-6">
            <div className="font-display font-black uppercase tracking-widest text-[10px] text-[#FF6B00]">
              Internal Review
            </div>
            <div className="mt-1 font-display font-bold text-sm text-white">
              Finds and Fades are under founder review.
            </div>
            <div className="mt-1 text-xs text-white/60 font-mono">
              Available on next release.
            </div>
          </div>
        </div>
      )}
      {isAdmin && (
        <div
          className="mt-2 text-[9px] uppercase tracking-widest text-[#FF6B00] font-display font-bold"
          data-testid="edge-lists-admin-flag"
        >
          Admin preview - internal review only
        </div>
      )}
      {error && !loading && canSeeLists && (
        <div className="mt-3 text-xs text-[#FF6B00] font-mono" data-testid="edge-list-error">
          {error}
        </div>
      )}
      {selected && (
        <EdgePlayerModal
          edge={selected}
          onClose={() => setSelected(null)}
          leagueId={leagueId}
          seasonYear={seasonYear}
          scoringFormat={scoringFormat}
          isDark={isDark}
        />
      )}
    </section>
  );
}
